// src/pages/imamMahdi/WhyOccultation.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const WhyOccultation = () => {
  return (
    <>
      <Helmet>
        <title>Why Occultation of Imam Mahdi (ajtf)? | Ziyarat Guide</title>
        <meta
          name="description"
          content="Understand the divine wisdom behind the Occultation (Ghaybah) of Imam Mahdi (ajtf) — its history, purpose, and meaning in Islamic belief."
        />
        <meta
          property="og:title"
          content="Why Occultation of Imam Mahdi (ajtf)? | Ziyarat Guide"
        />
        <meta
          property="og:description"
          content="Explore the reasons and significance of Imam Mahdi’s Minor and Major Occultation — a period of divine protection, spiritual testing, and preparation for justice."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/07/third-His-role-as-the-ultimate-reformer-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">Why Occultation?</h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/07/third-His-role-as-the-ultimate-reformer-.png"
              alt="Occultation of Imam Mahdi"
              className="imam-image"
            />

            <h2>What Does “Occultation” Mean?</h2>
            <p>
              In Shia teachings, “occultation” (Arabic: Ghaybah) refers to a
              divinely decreed period in which Imam Mahdi (aj) lives among us,
              yet remains hidden from public view. He continues to guide in
              unseen ways, but without openly leading the community.
            </p>
            <p>
              This concept is rooted in numerous authentic narrations from the
              Prophet Muhammad (peace be upon him and his family) and the Imams
              of Ahlulbayt (as).
            </p>

            <h2>A Short History: Minor & Major Occultation</h2>

            <h3>1. Minor Occultation (Ghaybat al-Sughra)</h3>
            <ul>
              <li>
                Began in 260 AH (874 CE) immediately after the martyrdom of his
                father, Imam Hasan al-Askari (as).
              </li>
              <li>Lasted about 69 years.</li>
              <li>
                During this time, Imam Mahdi (aj) maintained contact with his
                followers through four successive special deputies (Safeers).
              </li>
              <li>
                These trusted representatives conveyed the Imam’s guidance,
                answered questions, and collected religious dues.
              </li>
            </ul>
            <p>The four deputies were:</p>
            <ol>
              <li>Uthman ibn Sa’id al-Amri</li>
              <li>Muhammad ibn Uthman</li>
              <li>Husayn ibn Ruh</li>
              <li>Ali ibn Muhammad al-Samarri</li>
            </ol>
            <p>
              When the fourth deputy passed away in 329 AH (941 CE), he
              delivered a final letter from the Imam, announcing the beginning
              of the Major Occultation.
            </p>

            <h3>2. Major Occultation (Ghaybat al-Kubra)</h3>
            <ul>
              <li>Started in 941 CE and continues to this day.</li>
              <li>In this period, there are no appointed deputies.</li>
              <li>
                The Imam instructed the community to turn to the most learned
                scholars (Mujtahids / Maraji‘) for religious guidance,
                safeguarding the faith until his return.
              </li>
            </ul>

            <h2>Why Did Allah Decree the Occultation?</h2>
            <ul>
              <li>
                <strong>Divine protection:</strong> Tyrannical rulers of the
                time sought to wipe out the lineage of Imamate to prevent the
                fulfilment of prophecies about a saviour. The occultation
                preserved Imam Mahdi’s life, ensuring he would reappear when the
                world is truly ready.
              </li>
              <li>
                <strong>Testing believers’ sincerity:</strong> The occultation
                is a profound trial — separating those who hold firm to faith
                and await truth with patience, from those who waver. <br />
                “Do people think they will be left alone because they say: ‘We
                believe,’ and will not be tested?” (Qur’an 29:2)
              </li>
              <li>
                <strong>Preparation of humanity:</strong> The Imam’s long
                occultation allows humanity to reach a point where they
                recognise the need for divine justice, preparing hearts to
                receive him wholeheartedly when he returns.
              </li>
              <li>
                <strong>
                  Strengthening the community’s intellectual and spiritual
                  independence:
                </strong>{" "}
                In the absence of direct leadership, the Shia community
                developed rich traditions of scholarship (Ijtihad, Usul al-Fiqh)
                ensuring the faith’s resilience.
              </li>
            </ul>

            <h2>Occultation Does Not Mean Abandonment</h2>
            <p>
              It’s essential to understand that Imam Mahdi’s occultation is not
              an absence of care. Numerous narrations affirm that he:
            </p>
            <ul>
              <li>
                Prays for his followers, intercedes for them, and watches over
                their affairs.
              </li>
              <li>
                Guides the hearts of scholars and sincere believers.
              </li>
              <li>
                Will return at the divinely appointed time to establish justice.
              </li>
            </ul>

            <p>
              Imam Mahdi (aj) entered occultation by Allah’s wisdom – first a
              Minor Occultation through four deputies, then a Major Occultation
              without direct representatives. This protects his life, tests and
              purifies believers, and readies the world for the establishment of
              divine justice.
            </p>
            <p>
              For Muslims, the occultation is a period of patient hope and
              active preparation, striving to live by truth and justice so they
              may stand alongside the Imam when he returns.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/described-in-quran-hadith">
                Described In Quran & Hadith
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/how-to-talk">How To Talk</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">
                Impact of His Appearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">
                Practices & Allegiance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">
                Reflections & Stories
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">
                Signs of Reappearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default WhyOccultation;
